import CookieConsentManager from './Cookies/CookieConsentManager';
import Dispatcher from './Dispatcher';
import { MessageType, SessionState, TestState, WebCamState } from './Enums';
import MessageService from './MessageService';
import PrivacyPriorityScreen from './Screens/PrivacyPriorityScreen';
import ResultCollector from './ResultCollector';
import SessionService from './SessionService';
import Test from './Test';
import WebCamAccessManager from './WebCamAccessManager';
import { UiService } from './UiService';
import WebCamAccessScreen from './Screens/WebCamAccessScreen';
import ErrorService from './ErrorService';
import FaceService from './FaceService';
import Environment from './Environment';
import WelcomeScreen from './Screens/WelcomeScreen';

export default class IntegrationService {
    /**
     * Integration service constructor
     * @param {Dispatcher} dispatcher
     * @param {SessionService} sessionService
     * @param {ProxyRecorder} proxyRecorder
     * @param {UrlHelper} urlHelper
     * @param {Test} test
     * @param {WebCamAccessManager} webcamAccessManager
     * @param {ResultCollector} resultCollector
     * @param {CookieConsentManager} cookieConsentManager
     * @param {MessageService} messageService
     * @param {UiService} uiService
     * @param {ErrorService} errorService,
     * @param {FaceService} faceService
     * @param {Environment} environment
     */
    constructor(
        dispatcher,
        sessionService,
        proxyRecorder,
        urlHelper,
        test,
        webcamAccessManager,
        resultCollector,
        cookieConsentManager,
        messageService,
        uiService,
        errorService,
        faceService,
        environment
    ) {
        this.dispatcher = dispatcher;
        this.sessionService = sessionService;
        this.proxyRecorder = proxyRecorder;
        this.test = test;
        this.webcamAccessManager = webcamAccessManager;
        this.resultCollector = resultCollector;
        this.cookieConsentManager = cookieConsentManager;
        this.messageService = messageService;
        this.uiService = uiService;
        this.errorService = errorService;
        this.faceService = faceService;
        this.environment = environment;

        this.isQualityCheckSucceeded = false;
        this.qualityCheckStarted = false;
        this.listenedOrigin = urlHelper.getParameter('origin');
        this.config = {};
        this.options = {};

        window.addEventListener('message', async (event) => {
            await this._onMessageReceivedHandler(event);
        });

        this.sessionService.on('sessionCreated', (sessionData) => {
            if (sessionData.sessionStateID !== SessionState.fail) {
                this.sendFeedback({
                    isInitSignal: true,
                    sessionId: sessionData.sessionID,
                    sessionGuid: sessionData.sessionGuid,
                    orig: window.location.href
                });
            }
        });

        this.sessionService.on('sessionLogged', (data) => {
            this.sendFeedback(data);
        });

        this.sessionService.on('sessionStateChanged', (data) => {
            this.sendFeedback(data);
        });

        this.test.on('recorderError', (message) => {
            this.sendFeedback({
                messageType: MessageType.feedback.COMMAND_FAILED,
                data: {
                    state: 'failed',
                    reason: message
                }
            });
        });
    }

    waitForConfig() {
        return new Promise((resolve) => {
            const wait = () => {
                if (this.config && this.config.initial) {
                    return resolve(this.config.initial.environmentCheckerResults);
                }

                setTimeout(wait, 100);
            };

            wait();
        });
    }

    async _onMessageReceivedHandler(e) {
        if (e.data && e.origin === this.listenedOrigin) {
            try {
                //     console.debug('[IntegrationService] Received command:', e.data);
                let msg;
                if (typeof e.data === 'string' || e.data instanceof String) {
                    msg = JSON.parse(e.data);
                } else {
                    // e.data is an object, it doesn't have to be parsed
                    msg = e.data;
                }

                if (msg) {
                    this._processCommand(msg);
                }
            } catch (err) {
                console.error('[IntegrationService] Error while processing external command:', err);
            }
        }
    }

    async _processCommand(command) {
        if (command.isInitSignal && command.config) {
            const configResponse = command.configResponse || command.config.configResponse;
            this._overrideParameters(command.config, configResponse);

            return;
        }

        switch (command.messageType) {
            case MessageType.command.SET_PLAYLIST:
                await this._setPlaylist(command);
                break;
            case MessageType.command.PLAY_NEXT:
                await this._playNext(command);
                break;
            case MessageType.command.STOP_PLAYING:
                await this._stopPlaying(command);
                break;
            case MessageType.command.COMPLETE:
                await this._complete(command);
                break;
            case MessageType.command.PLAY:
                await this._play(command);
                break;
            case MessageType.command.PRELOAD_MEDIA:
                await this._preloadMedia(command);
                break;
            case MessageType.command.ADD_STIMULUSINFO:
                this._addStimulusInfo(command);
                break;
            case MessageType.command.ADD_POSTPROCESSING_ACTION:
                await this._addPostProcessingAction(command);
                break;
            case MessageType.command.TERMINATE:
                await this._terminate(command);
                break;
            case MessageType.command.START_RECORDING:
                await this._startRecording(command);
                break;
            case MessageType.command.STOP_RECORDING:
                await this._stopRecording(command);
                break;
            case MessageType.command.SHOW_WEBCAM_PERMISSION_SCREEN:
                await this._showWebcamPermissionScreen(command);
                break;
            case MessageType.command.ASK_WEBCAM_ACCESS_AND_CHECK_QUALITY:
                await this._askWebcamAccessAndCheckQuality(command);
                break;
            case MessageType.command.ASK_WEBCAM_ACCESS:
                await this._askWebcamAccess(command);
                break;
            case MessageType.command.CHECK_WEBCAM_QALITY:
                await this._startQualityCheck(command);
                break;
            case MessageType.command.GET_SESSION_INFO:
                await this._getSessionInfo(command);
                break;
            case MessageType.command.SHOW_PRIVACY:
                await this._showPrivacy(command);
                break;
            case MessageType.command.COOKIE_CONSENT_PROVIDED:
                await this._cookieConsentProvided(command);
                break;
        }
    }

    _overrideParameters(config, configResponse) {
        // aspnetcore serializes to camel case by default, but to keep backward compatibility
        // DC Integration maps configResponse.testElementSequence to PascalCase. We have to map
        // it back to camelCase here.
        if (configResponse) {
            configResponse = this._mapTestElementSequencePropertiesToCamelCase(configResponse);
        }
        this.config = { initial: config, response: configResponse };
        if (this.config.initial.startOptions && this.config.initial.startOptions.qualityFeedbackType) {
            this.options.qualityFeedbackType = this.config.initial.startOptions.qualityFeedbackType;
        }

        if (this.config.initial.startOptions && this.config.initial.startOptions.externalCssUrl) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.type = 'text/css';
            link.href = this.config.initial.startOptions.externalCssUrl;
            document.head.appendChild(link);
        }

        const setTestElements = async () => {
            if (this.sessionService.sessionId) {
                //TODO: REFACTOR THIS ASAP
                if (configResponse.testElementSequence.elements.some((x) => x.testElementTypeID === 10)) {
                    await this.dispatcher.getTestElements();
                } else {
                    await this.dispatcher.setTestElements(configResponse.testElementSequence);
                }

                this.sendFeedback({
                    isInitSignal: true,
                    isConfig: true,
                    configResponse: configResponse,
                    orig: window.location.href
                });
            } else {
                setTimeout(setTestElements, 100);
            }
        };

        setTestElements();

        if (this.config.reallowScreenDisabled) {
            jsModuleSettings.showReAllow = !this.config.reallowScreenDisabled;
            jsModuleSettings.autoDenyTime = jsModuleSettings.autoDenyTime || 1000;
        }

        if (this.config.customAllowScreenEnabled) {
            jsModuleSettings.showAutoAllow = this.config.customAllowScreenEnabled;
            jsModuleSettings.autoAllowTime = jsModuleSettings.autoAllowTime || 1000;
        }

        const metadata = this.config.initial;
        if (metadata.progressValue) {
            this.options.progressValue = metadata.progressValue;
            const pv = document.getElementById('progress-value');
            if (pv) {
                pv.style.width = metadata.progressValue + '%';
            }

            const pp = document.getElementById('progress-percentage');
            if (pp) {
                pp.innerHTML = metadata.progressValue + '%';
            }
        }

        const translations = metadata.translations;
        if (translations) {
            Array.from(document.querySelectorAll('[data-ui-message-key]')).forEach((element) => {
                const translationKey = element.dataset.uiMessageKey;
                const translation = translations.find((t) => t.key === translationKey || t[0] === translationKey);

                if (typeof translation !== 'undefined') {
                    element.innerHTML = translation.value;
                }
            });

            translations.forEach((translation) => {
                if (translation.key && this.messageService.uiMessages[translation.key]) {
                    this.messageService.uiMessages[translation.key] = translation.value;
                }

                if (translation[0] && this.messageService.uiMessages[translation[0]]) {
                    this.messageService.uiMessages[translation[0]] = translation[1];
                }
            });
        }
    }

    _mapTestElementSequencePropertiesToCamelCase(data) {
        const objectKeysToCamelCase = (input) => {
            if (typeof input !== 'object') return input;
            if (Array.isArray(input)) return input.map(objectKeysToCamelCase);
            return Object.keys(input).reduce((newObj, key) => {
                const val = input[key];
                const newVal = typeof val === 'object' ? objectKeysToCamelCase(val) : val;
                const newKey = key[0].toLowerCase() + key.slice(1);
                newObj[newKey] = newVal;
                return newObj;
            }, {});
        };

        data.testElementSequence = objectKeysToCamelCase(data.testElementSequence);

        return data;
    }

    async _setPlaylist(command) {
        if (command.metadata) {
            const newConfig = this.config.initial;
            newConfig.elements = command.metadata;
            const collectionResult = await this._post(newConfig.apiKey, newConfig);
            if (collectionResult.succeeded) {
                newConfig.configResponse = collectionResult.data;
                //TODO: REFACTOR THIS ASAP
                if (newConfig.configResponse.testElementSequence.some((x) => x.testElementTypeID === 10)) {
                    await this.dispatcher.getTestElements();
                } else {
                    await this.dispatcher.setTestElements(newConfig.configResponse.testElementSequence);
                }
                await this.test.preload();
                this._commandFinished(command, newConfig.configResponse);
            } else {
                this._commandFailed(command, collectionResult);
            }
        }
    }

    async _playNext(command) {
        this._commandStarted(command);

        await this._showQualityCheck(command);
        if (!this.isQualityCheckSucceeded) {
            this._commandFailed(command);
            return;
        }

        const playNextStateChangedHandler = (event) => {
            if (event.state == TestState.finishPlaying) {
                this.test.off('testStateChanged', playNextStateChangedHandler);

                this._commandFinished(command, event);
            } else {
                this._commandInProgress(command, event);
            }
        };

        this.test.on('testStateChanged', playNextStateChangedHandler);

        await this.test.playNext();
    }

    async _stopPlaying(command) {
        const testElement = await this.test.stopCurrentTestElement();

        this._commandFinished(command, {
            state: 'stopped',
            element: testElement
        });
    }

    async _complete(command) {
        this._commandStarted(command);

        await this.test.complete(command.metadata.enableRedirect || false);

        this._commandFinished(command, {
            state: 'finished'
        });
    }

    async _play(command) {
        this._commandStarted(command);

        await this._showQualityCheck(command);
        if (!this.isQualityCheckSucceeded) {
            this._commandFailed(command, {
                state: 'failed'
            });
            return;
        }

        const playNextStateChangedHandler = (event) => {
            if (event.state == TestState.finished) {
                this.test.off('testStateChanged', playNextStateChangedHandler);
            } else {
                this._commandInProgress(command, event);
            }
        };

        this.test.on('testStateChanged', playNextStateChangedHandler);

        // Init test event handlers
        this.test.startTest();

        // Start recording
        await this.test.startRecording();

        // Play all
        await this.test.playAll();

        // Stop recording
        await this.test.stopRecording();

        // Finish the test
        await this.test.complete();

        this._commandFinished(command, {
            state: 'finished'
        });
    }

    async _preloadMedia(command) {
        this._commandStarted(command);

        await this.test.preload();

        this._commandFinished(command, {
            state: 'finished'
        });
    }

    _addStimulusInfo(command) {
        this.resultCollector.addInfo(command.metadata);

        this._commandFinished(command, {
            state: 'finished'
        });
    }

    async _addPostProcessingAction(command) {
        if (command.metadata && command.metadata.url && command.metadata.trigger) {
            await this.sessionService.updateSession({
                postActionUrl: command.metadata.url,
                postActionTrigger: command.metadata.trigger
            });

            this._commandFinished(command);
        }
    }

    async _terminate(command) {
        const terminateReason =
            command.metadata && command.metadata.reason
                ? command.metadata.reason
                : 'Declined from integration API with unknown reason';

        const sessionState = this.sessionService.lastSessionStateId;
        if (sessionState !== SessionState.declined && sessionState !== SessionState.fail) {
            await this.sessionService.updateSession({
                stateId: SessionState.declined,
                reasonMessage: terminateReason
            });
        }

        this._commandFinished(command);
    }

    async _startRecording(command) {
        await this.test.startRecording();

        this._commandFinished(command, {
            state: 'finished'
        });
    }

    async _stopRecording(command) {
        await this.test.stopRecording();

        this._commandFinished(command, {
            state: 'finished'
        });
    }

    async _showWebcamPermissionScreen(command) {
        this._commandStarted(command);

        const webcamAccessScreen = new WebCamAccessScreen(
            this.sessionService,
            this.messageService,
            this.proxyRecorder,
            this.uiService
        );
        await webcamAccessScreen.display();

        this._commandFinished(command, {
            state: WebCamState.gotIt,
            reason: 'user pressed got-it button'
        });
    }

    async _askWebcamAccessAndCheckQuality(command) {
        this._commandStarted(command);

        const wacqWebCamStateChangeHandler = (event) => {
            this._commandInProgress(command, event.data);
        };

        this.webcamAccessManager.on('webcamStateChanged', wacqWebCamStateChangeHandler);

        const wacqWebCamAccessState = await this.webcamAccessManager.askWebcamPermissionsWithUI();

        this.webcamAccessManager.off('webcamStateChanged', wacqWebCamStateChangeHandler);

        if (wacqWebCamAccessState === WebCamState.denied) {
            this.errorService.webCamDenied();
            this._commandFailed(command, { state: WebCamState.denied, environment: this.environment.detectorResult });

            return;
        }
        await this._showQualityCheck(command);

        if (!this.isQualityCheckSucceeded) {
            this._commandFailed(command, { state: WebCamState.failed });
        } else {
            this._commandFinished(command, { state: WebCamState.readyToUse });
        }
    }

    async _askWebcamAccess(command) {
        this._commandStarted(command);

        const awWebCamStateChangeHandler = (event) => {
            this._commandInProgress(command, event.data);
        };

        this.webcamAccessManager.on('webcamStateChanged', awWebCamStateChangeHandler);

        const awWebCamAccessState = await this.webcamAccessManager.askWebcamPermissionsWithUI();
        this.webcamAccessManager.off('webcamStateChanged', awWebCamStateChangeHandler);

        if (awWebCamAccessState === WebCamState.denied) {
            this.errorService.webCamDenied();
            this._commandFailed(command, { state: awWebCamAccessState, environment: this.environment.detectorResult });
        } else {
            const commandData = { state: awWebCamAccessState };

            if (awWebCamAccessState == WebCamState.autoDenied) {
                commandData.isAutoDeny = true;
            }

            this._commandFinished(command, commandData);
        }
    }

    async _startQualityCheck(command) {
        this._commandStarted(command);

        await this._showQualityCheck(command);

     

      //  return this.isQualityCheckSucceeded;
    }

    async _showQualityCheck(command) {
        if (this.webcamAccessManager.permissionAllowed && !this.qualityCheckStarted) {
            this.qualityCheckStarted = true;
            const qualityCheckState = await this.webcamAccessManager.showQualityCheckScreen();
            this.isQualityCheckSucceeded  = qualityCheckState.state !== WebCamState.denied && qualityCheckState.state !== WebCamState.failed
            if (!this.isQualityCheckSucceeded) {
                this._commandFailed(command,qualityCheckState);
            } else {
                this._commandFinished(command,qualityCheckState);
            }
        }
    }

    async _getSessionInfo(command) {
        const devices = await this.proxyRecorder.getAvailableDevices();
        const cameras = { cameras: devices.video || [] };
        const microphones = { microphones: devices.audio || [] };

        const reason = this.sessionService.lastReason;

        const result = {
            sessionId: this.sessionService.sessionId,
            stateId: this.sessionService.lastSessionStateId,
            reasonId: reason?.id,
            reasonName: reason?._messageKey,
            devicesInfo: { cameras: cameras, mics: microphones }
        };

        this._commandFinished(command, result);
    }

    async _showPrivacy(command) {
        const cookieConsentResult = await this.cookieConsentManager.getCookieConsent();
        if (!cookieConsentResult) {
            this.errorService.cookieConsentDenied();

            this._commandFailed(command.id, 'cookie policy declined');
        }

        if (!jsModuleSettings.jumpToQualityScreen) {
            await new WelcomeScreen(this.uiService).display();

            const privacyPriorityScreen = new PrivacyPriorityScreen(this.uiService);
            await privacyPriorityScreen.display();
        }

        this._commandFinished(command);
    }

    async _cookieConsentProvided(command) {
        try {
            await this.cookieConsentManager.cookieConsentProvided(
                command.metadata.result,
                command.metadata.consentText
            );
            this._commandFinished(command);
        } catch (e) {
            this._commandFailed(command);
        }
    }

    _commandStarted(command) {
        this.sendFeedback({ sourceCommandId: command.id, messageType: MessageType.feedback.COMMAND_STARTED });
    }

    _commandInProgress(command, data) {
        this.sendFeedback({
            sourceCommandId: command.id,
            messageType: MessageType.feedback.COMMAND_IN_PROGRESS,
            data: data
        });
    }

    _commandFinished(command, data) {
        this.sendFeedback({
            sourceCommandId: command.id,
            messageType: MessageType.feedback.COMMAND_FINISHED,
            data: data
        });
    }

    _commandFailed(command, data) {
        this.sendFeedback({
            sourceCommandId: command.id,
            messageType: MessageType.feedback.COMMAND_FAILED,
            data: data
        });
    }

    sendFeedback(feedback) {
        window.parent.postMessage(JSON.stringify(feedback), this.listenedOrigin);
    }

    async _post(apiKey, configuration) {
        try {
            const response = await fetch(`${window.location.origin}\\collection`, {
                method: 'POST',
                body: JSON.stringify(configuration),
                headers: {
                    'Content-Type': 'application/json',
                    AccessKey: apiKey
                }
            });

            //TODO: check response.status
            return { data: await response.json(), succeeded: response.ok };
        } catch (e) {
            // network error
            this.sessionService.logMessage('error', 'PostCollection error:' + e.message);
        }
    }
}
